<template>
  <div class="Footer1">
    <div class="pagination-wrapper" :class="{'pagination-wrapper--compact': compact}">
      <p v-if="compact" class="pagination-summary" aria-live="polite">
        Page <strong>{{ options.page }}</strong> of <strong>{{ lastPage }}</strong>
        <span class="pagination-summary__sep">·</span>
        Showing <strong>{{ recordFrom }}–{{ recordTo }}</strong> of
        <strong>{{ noOfRecords }}</strong>
      </p>

      <div class="pagination-controls">
        <button
          type="button"
          class="Button"
          :disabled="loading || options.page <= 1"
          @click="goToPage(options.page - 1)"
        >
          Previous
        </button>

        <button
          v-for="pageNum in visiblePages"
          :key="`page-${pageNum}`"
          type="button"
          class="Button1"
          :class="{ 'Button1--active': pageNum === options.page }"
          :disabled="loading"
          @click="goToPage(pageNum)"
        >
          {{ pageNum }}
        </button>

        <button
          type="button"
          class="Button"
          :disabled="loading || options.page >= lastPage"
          @click="goToPage(options.page + 1)"
        >
          Next
        </button>
      </div>

      <div v-if="!compact" class="display-section">
        <span>Display</span>
        <label>{{ recordFrom }} - {{ recordTo }} of {{ noOfRecords }}</label>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/plugins/axios";

export default {
  props: {
    url: {
      type: String,
      default: "",
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Number,
      default: 0,
    },
    pageSize: {
      type: Number,
      default: 15,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    deferLoad: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      noOfRecords: 0,
      requestId: 0,
      options: {
        page: 1,
        perPage: 15,
        total: 0,
        totalItems: 0,
      },
    };
  },
  computed: {
    lastPage() {
      const total = Number(this.options.total);
      return Number.isFinite(total) && total > 0 ? total : 1;
    },
    recordFrom() {
      if (this.noOfRecords === 0) return 0;
      return (this.options.page - 1) * this.options.perPage + 1;
    },
    recordTo() {
      if (this.noOfRecords === 0) return 0;
      return Math.min(this.options.page * this.options.perPage, this.noOfRecords);
    },
    visiblePages() {
      const total = this.lastPage;
      const current = this.options.page;
      const maxVisible = 5;
      if (total <= maxVisible) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      let start = Math.max(1, current - 2);
      let end = Math.min(total, start + maxVisible - 1);
      start = Math.max(1, end - maxVisible + 1);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  watch: {
    options: {
      handler(val) {
        this.$emit("paginationOptions", val);
      },
      deep: true,
    },
    url() {
      this.goToPage(1, true);
    },
    filter: {
      handler() {
        this.goToPage(1, true);
      },
      deep: true,
    },
    pageSize(size) {
      this.options.perPage = Number(size) || 15;
      this.goToPage(1);
    },
  },
  created() {
    this.options.perPage = Number(this.pageSize) || 15;
    if (!this.deferLoad) {
      this.goToPage(1);
    }
  },
  methods: {
    refreshData() {
      this.goToPage(this.options.page, true);
    },
    goToPage(page, force = false) {
      const target = Number(page);
      if (!Number.isFinite(target) || target < 1) {
        return;
      }

      const knownLastPage = Number(this.options.total);
      const targetPage =
        knownLastPage > 0 ? Math.min(target, knownLastPage) : target;

      if (!force && targetPage === this.options.page && this.noOfRecords > 0) {
        return;
      }
      this.requestPage(targetPage);
    },
    requestPage(page) {
      if (!this.url) {
        this.applyPagedResponse(null);
        return;
      }

      const targetPage = Math.max(1, Number(page) || 1);
      const requestId = ++this.requestId;

      this.loading = true;
      this.$emit("loading", true);

      axios
        .get(this.url, {
          params: {
            page: targetPage,
            size: this.options.perPage,
          },
        })
        .then((res) => {
          if (requestId !== this.requestId) {
            return;
          }
          this.applyPagedResponse(res, targetPage);
        })
        .catch(() => {
          if (requestId !== this.requestId) {
            return;
          }
          this.applyPagedResponse(null, 1);
        })
        .finally(() => {
          if (requestId !== this.requestId) {
            return;
          }
          this.loading = false;
          this.$emit("loading", false);
        });
    },
    applyPagedResponse(res, requestedPage = 1) {
      if (res && res.data && res.data.meta) {
        const meta = res.data.meta;
        const totalItems = Number(meta.totalItems) || 0;
        const lastPage = Math.max(1, Number(meta.lastPage) || 1);
        const currentPage = Number(meta.currentPage) || requestedPage;

        this.noOfRecords = totalItems;
        this.options.total = lastPage;
        this.options.totalItems = totalItems;
        this.options.page = Math.min(
          Math.max(1, currentPage),
          lastPage
        );
        this.$emit("response", Array.isArray(res.data.data) ? res.data.data : []);
        return;
      }

      this.noOfRecords = 0;
      this.options.total = 1;
      this.options.totalItems = 0;
      this.options.page = 1;
      this.$emit("response", []);
    },
  },
};
</script>

<style scoped>
.Footer1 {
  margin-top: 0;
  border-top: none;
  padding-top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(12px, 3vw, 25px);
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
}

.Button {
  border: none;
  background: var(--brand-primary);
  color: #fff;
  cursor: pointer;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  border-radius: 20px;
  transition: background 0.2s ease, opacity 0.2s ease;
  min-width: 72px;
}

.Button:hover:not(:disabled) {
  background: var(--brand-primary-hover, #477435);
}

.Button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.Button1 {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #334155;
  padding: 8px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 13px;
  min-width: 36px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.Button1:hover:not(:disabled) {
  border-color: var(--brand-primary-border);
  color: var(--brand-primary);
}

.Button1--active {
  background: var(--brand-primary);
  border-color: var(--brand-primary);
  color: #fff;
}

.Button1:disabled {
  cursor: wait;
  opacity: 0.7;
}

.display-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.display-section label {
  font-size: 13px;
  color: #555;
  margin: 0;
}

.pagination-wrapper--compact {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.pagination-summary {
  margin: 0;
  text-align: center;
  font-size: 12px;
  line-height: 1.4;
  color: #475569;
}

.pagination-summary strong {
  color: #0f172a;
  font-weight: 700;
}

.pagination-summary__sep {
  margin: 0 4px;
  color: #94a3b8;
}

.pagination-wrapper--compact .pagination-controls {
  width: 100%;
  justify-content: center;
}

@media (max-width: 768px) {
  .pagination-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .pagination-controls {
    width: 100%;
  }

  .display-section {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .Button,
  .Button1 {
    font-size: 12px;
    padding: 7px 10px;
    min-width: 32px;
  }

  .Button {
    min-width: 64px;
  }

  .display-section {
    font-size: 12px;
    flex-wrap: wrap;
    white-space: normal;
    text-align: center;
  }
}
</style>
